/**
 * Responsibility: present the pause menu and report what the player chose.
 *
 * Plain DOM, built and styled here so the whole feature is one file. It owns its elements and its
 * listeners, and it contains **no simulation logic** — every action is a callback handed in by the
 * caller. The menu does not know what a seed does, only that the player typed one and pressed a
 * button.
 *
 * That separation is what keeps the flight loop unaware the menu exists. Nothing here reads or writes
 * flight state, tile residency, or settings storage.
 */

import { AIRCRAFT } from '../flight/aircraft/index.js';
import { BLOOM, INPUT, HUD, DEBUG } from '../config.js';
import { createLogo } from './logo.js';

/** Milliseconds per second, for the one place here that needs a timer. */
const MS_PER_SECOND = 1000;

/** Panel styling shared by every control, kept in one place so the menu looks of a piece. */
const ACCENT = 'rgba(140, 232, 255, 0.9)';
const DIM = 'rgba(150, 200, 230, 0.55)';

/**
 * Logo size in the title row, sized to the two lines of text beside it rather than picked.
 *
 * The name at 18px over the seed at 11px comes to roughly this tall, so the mark reads as part of the
 * heading instead of an image that happens to sit next to it.
 */
const LOGO_SIZE_PX = 36;

/** Glow behind the mark. Dimmer than the mark itself, so it suggests bloom without looking lit. */
const LOGO_GLOW = 'rgba(140, 232, 255, 0.3)';

/**
 * Build an element with styles and properties in one call, to keep the layout readable below.
 *
 * Every element gets `box-sizing: border-box`. Without it, a control at `width: 100%` with padding is
 * wider than its container by exactly its own padding, and the panel grows a horizontal scrollbar —
 * which is what happened. Setting it here rather than on each control means anything added later
 * cannot reintroduce the same bug.
 */
function el(tag, styles = {}, props = {}) {
  const node = document.createElement(tag);
  Object.assign(node.style, { boxSizing: 'border-box' }, styles);
  Object.assign(node, props);
  return node;
}

/** A clickable row. Returns the button so callers can enable, disable, or highlight it. */
function makeButton(label, onClick) {
  const button = el(
    'button',
    {
      display: 'block',
      width: '100%',
      padding: '9px 12px',
      marginBottom: '6px',
      background: 'rgba(20, 40, 60, 0.55)',
      border: '1px solid rgba(140, 232, 255, 0.28)',
      borderRadius: '2px',
      color: ACCENT,
      font: 'inherit',
      textAlign: 'left',
      cursor: 'pointer',
    },
    { type: 'button', textContent: label },
  );

  button.addEventListener('click', onClick);
  button.addEventListener('mouseenter', () => {
    if (!button.disabled) button.style.background = 'rgba(30, 70, 100, 0.7)';
  });
  button.addEventListener('mouseleave', () => {
    button.style.background = 'rgba(20, 40, 60, 0.55)';
  });

  return button;
}

/**
 * The keys worth telling a first-time player about.
 *
 * Built from the bindings rather than written out, so rebinding a key in config cannot leave the menu
 * describing a control that no longer exists. `KeyboardEvent.code` values are not readable as-is, hence
 * the small amount of prettifying.
 */
export function controlHints() {
  const pretty = (code) =>
    ({
      BracketLeft: '[',
      BracketRight: ']',
      Comma: ',',
      Period: '.',
      Escape: 'Esc',
    })[code] ?? code.replace('Key', '');

  return [
    'WASD or arrows to fly',

    /*
      The same keys mean different things on the rails, and saying so is cheaper than two lists. Left and
      right become looking around because there is nothing to steer — that is the point of being on a train.
    */
    'on the rails: left and right to look around',
    `${pretty(INPUT.TRIM_DOWN_KEY)} slower · ${pretty(INPUT.TRIM_UP_KEY)} faster · ${pretty(INPUT.TRIM_DOWN_KEY)} to a stand on the rails`,
    `${pretty(INPUT.TOGGLE_VIEW_KEY)} for the cab or outside view, on the rails`,

    /*
      Listed because the camera tilt is otherwise undiscoverable — it has no slider, and the default is no
      longer level, so a player who wants the old framing has no way to find it without being told.
    */
    `${pretty(INPUT.CAMERA_TILT_UP_KEY)} · ${pretty(INPUT.CAMERA_TILT_DOWN_KEY)} camera tilt`,
    `${pretty(INPUT.CYCLE_AIRCRAFT_KEY)} to change aircraft`,
    `${pretty(DEBUG.TOGGLE_KEY)} for the developer readout`,
    `${pretty(INPUT.PAUSE_KEY)} for this menu`,
  ];
}

/** A section heading. */
function makeHeading(text) {
  return el(
    'div',
    {
      margin: '18px 0 8px',
      color: DIM,
      fontSize: '11px',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    },
    { textContent: text },
  );
}

/** A labelled checkbox row. */
function makeCheckbox(label, checked, onChange) {
  const row = el('label', {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '5px 2px',
    color: ACCENT,
    cursor: 'pointer',
  });

  const box = el('input', {}, { type: 'checkbox', checked });
  box.addEventListener('change', () => onChange(box.checked));

  row.append(box, el('span', {}, { textContent: label }));
  return { row, box };
}

/**
 * A labelled slider row with a live value readout.
 *
 * Returns the parts rather than just the row, so `open` can refresh them by reference. Finding them
 * again with a CSS selector would work until someone added another `span` to the header.
 */
function makeSlider(label, { value, min, max, step, format = (v) => v.toFixed(2) }, onChange) {
  const row = el('div', { padding: '5px 2px', color: ACCENT });
  const header = el('div', { display: 'flex', justifyContent: 'space-between' });
  const readout = el('span', { color: DIM }, { textContent: format(value) });

  header.append(el('span', {}, { textContent: label }), readout);

  const slider = el(
    'input',
    { width: '100%', marginTop: '4px', marginLeft: '0', marginRight: '0', accentColor: '#8ce8ff' },
    { type: 'range', min: String(min), max: String(max), step: String(step), value: String(value) },
  );

  slider.addEventListener('input', () => {
    const next = Number(slider.value);
    readout.textContent = format(next);
    onChange(next);
  });

  row.append(header, slider);
  return { row, slider, readout, format };
}

/**
 * Create the pause menu.
 *
 * @param {object} options
 * @param {HTMLElement} [options.parent] - Defaults to `document.body`.
 * @param {() => void} options.onResume
 * @param {() => void} options.onSave
 * @param {() => void} options.onLoad
 * @param {(seed: string) => void} options.onNewWorld - Receives the raw text; interpreting it is the
 *   caller's business, since `world/rng.js` is the authority on what a seed means.
 * @param {(aircraftId: string) => void} options.onSelectAircraft
 * @param {(step: number) => void} options.onSetCruiseStep - Step position, not a multiplier. The menu
 *   does not know what speeds the steps correspond to.
 * @param {(name: string, value: *) => void} options.onSetting
 * @returns {object}
 */
export function createMenu({
  parent = document.body,
  onResume,
  onSave,
  onLoad,
  onNewWorld,
  onSelectAircraft,
  onSetCruiseStep,
  onSetting,
  onToggleMode = () => {},
}) {
  const backdrop = el('div', {
    position: 'fixed',
    inset: '0',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.72)',
    // The canvas hides the cursor; the menu needs it back.
    cursor: 'default',
    zIndex: '100',
    font: '14px/1.5 ui-monospace, "Cascadia Code", Consolas, monospace',
  });

  const panel = el(
    'div',
    {
      /*
        Wide enough that nothing needs to scroll sideways. Vertical scrolling is fine — the panel has
        more rows than a short window can show — but a horizontal scrollbar in a menu is always a
        layout bug rather than a legitimate overflow.
      */
      width: '400px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '86vh',
      overflowY: 'auto',
      padding: '22px 24px 24px',
      background: 'rgba(6, 14, 24, 0.92)',
      border: '1px solid rgba(140, 232, 255, 0.22)',
      borderRadius: '3px',
      // No focus ring on the panel itself; it is focused programmatically, not tabbed to.
      outline: 'none',
    },
    /*
      Focusable, but not in the tab order. `open` focuses this so keyboard focus lands somewhere
      harmless — a div is not focusable at all without it, and the focus call would silently do nothing.
    */
    { tabIndex: -1 },
  );

  /*
    Title row: the mark, then the name with the seed under it.

    The logo is the same file the browser uses for the tab icon, recoloured from its indigo to the panel's
    cyan. Indigo is chosen to survive a white tab strip; against this panel's near-black it would sit too
    close to the background, and a mark that matches the border and the headings is the point of having
    one palette.
  */
  const titleRow = el('div', {
    display: 'flex',
    alignItems: 'center',
    gap: '13px',
    marginBottom: '16px',
  });

  const logo = createLogo({ size: LOGO_SIZE_PX, colour: ACCENT });
  /*
    Only added if it parsed. `createLogo` returns null rather than throwing, so a broken mark costs the
    menu its decoration and nothing else.
  */
  if (logo) {
    // The faintest glow, to place the mark in the same world as the terrain outside the panel. Static
    // and low: the whole experience is meant to be calm, so nothing here pulses or animates.
    logo.style.filter = `drop-shadow(0 0 6px ${LOGO_GLOW})`;
    titleRow.appendChild(logo);
  }

  const titleText = el('div');
  titleText.appendChild(
    el(
      'div',
      { color: ACCENT, fontSize: '18px', letterSpacing: '0.06em' },
      { textContent: 'Vibe Flyer' },
    ),
  );

  const seedLabel = el('div', { color: DIM, fontSize: '11px' });
  titleText.appendChild(seedLabel);

  titleRow.appendChild(titleText);
  panel.appendChild(titleRow);

  // --- flight -------------------------------------------------------------
  const resumeButton = makeButton('Resume', () => onResume());
  const saveButton = makeButton('Save world', () => onSave());
  const loadButton = makeButton('Load save', () => onLoad());
  panel.append(resumeButton, saveButton, loadButton);

  // --- mode ---------------------------------------------------------------
  /*
    One button rather than two, because there are exactly two ways to be in this world and a toggle that
    names the *other* one is the shortest way to say so. Its label is set by `open`.

    Near the top, above the world controls: which way you are travelling is a bigger decision than which
    aircraft you are in, and the same seed serves both.
  */
  const modeButton = makeButton('Ride the railway', () => onToggleMode());
  panel.appendChild(modeButton);

  // --- world --------------------------------------------------------------
  panel.appendChild(makeHeading('New world'));

  const seedField = el(
    'input',
    {
      width: '100%',
      padding: '8px 10px',
      marginBottom: '6px',
      background: 'rgba(10, 22, 34, 0.9)',
      border: '1px solid rgba(140, 232, 255, 0.22)',
      borderRadius: '2px',
      color: ACCENT,
      font: 'inherit',
    },
    { type: 'text', placeholder: 'seed — a number or a phrase', spellcheck: false },
  );

  /*
    Enter is the obvious way to submit a typed seed, and a player who has just typed one should not have
    to reach for the mouse.
  */
  seedField.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onNewWorld(seedField.value);
    }
  });

  panel.append(seedField, makeButton('Fly a new world', () => onNewWorld(seedField.value)));

  // --- cruise speed -------------------------------------------------------
  /*
    Duplicated deliberately: the bracket keys already do this in flight, but a control nobody can find
    may as well not exist. A labelled slider says what it is, shows the whole range, and needs no prior
    knowledge — the keys are then the fast way to do the same thing.
  */
  panel.appendChild(makeHeading('Cruise speed'));

  /*
    How many steps the trim range has. Held rather than captured, because the flight model owns that
    arithmetic and only tells us at `open` time — and the slider's own readout has to keep agreeing with
    it as the player drags.
  */
  let cruiseSteps = 1;

  const cruiseSlider = makeSlider(
    'Speed',
    // Range is filled in by `open`. These are placeholders until then.
    { value: 0, min: 0, max: 1, step: 1, format: (v) => `${v} of ${cruiseSteps}` },
    (step) => onSetCruiseStep(step),
  );
  panel.appendChild(cruiseSlider.row);

  // --- aircraft -----------------------------------------------------------
  panel.appendChild(makeHeading('Aircraft'));

  /** Button per aircraft, keyed by id, with the label kept alongside so `open` need not look it up. */
  const aircraftButtons = new Map();
  for (const aircraft of AIRCRAFT) {
    const button = makeButton(aircraft.label, () => onSelectAircraft(aircraft.id));
    aircraftButtons.set(aircraft.id, { button, label: aircraft.label });
    panel.appendChild(button);
  }

  // --- settings -----------------------------------------------------------
  panel.appendChild(makeHeading('Settings'));

  const bloomSlider = makeSlider(
    'Glow',
    {
      value: BLOOM.STRENGTH,
      min: BLOOM.MIN_STRENGTH,
      max: BLOOM.MAX_STRENGTH,
      step: BLOOM.STRENGTH_STEP,
    },
    (value) => onSetting('bloomStrength', value),
  );
  const invertPitch = makeCheckbox('Invert pitch', false, (value) =>
    onSetting('invertPitch', value),
  );
  const showOverlay = makeCheckbox('Debug readout', false, (value) =>
    onSetting('showOverlay', value),
  );

  panel.append(bloomSlider.row, invertPitch.row, showOverlay.row);

  // --- hints --------------------------------------------------------------
  panel.appendChild(makeHeading('Controls'));
  panel.appendChild(
    el(
      'div',
      { color: DIM, fontSize: '12px', whiteSpace: 'pre-line' },
      { textContent: controlHints().join('\n') },
    ),
  );

  backdrop.appendChild(panel);
  parent.appendChild(backdrop);

  /*
    Clicking the backdrop resumes, which is the least surprising behaviour for a modal and saves a
    trip to the button. Clicks inside the panel must not, hence the target check.
  */
  backdrop.addEventListener('click', (event) => {
    if (event.target === backdrop) onResume();
  });

  let visible = false;

  /** Pending label-restore timers, keyed by button, so a second press cancels the first. */
  const resultTimers = new Map();

  return {
    /**
     * Show the menu, refreshing everything it displays.
     *
     * State is pushed in at open time rather than held, so the menu never has a stale copy of anything.
     *
     * @param {object} state
     * @param {number|string} state.seed - Displayed so a liked world can be written down or shared.
     * @param {string} state.aircraftId
     * @param {boolean} state.hasSave - Whether a save exists, for enabling Load.
     * @param {object} state.settings
     * @param {{step: number, steps: number}} state.cruise - Current trim position and how many steps
     *   the range has. Supplied rather than computed, because the flight model owns that arithmetic.
     */
    open(state) {
      seedLabel.textContent = `seed  ${state.seed}`;

      /*
        Names where the button goes rather than where you are. "Ride the railway" while flying is
        unambiguous in a way that a checkbox labelled "train" is not.
      */
      modeButton.textContent = state.mode === 'train' ? 'Take to the air' : 'Ride the railway';

      cruiseSteps = state.cruise.steps;
      cruiseSlider.slider.min = '0';
      cruiseSlider.slider.max = String(cruiseSteps);
      cruiseSlider.slider.value = String(state.cruise.step);
      cruiseSlider.readout.textContent = cruiseSlider.format(state.cruise.step);

      loadButton.disabled = !state.hasSave;
      loadButton.style.opacity = state.hasSave ? '1' : '0.4';
      loadButton.style.cursor = state.hasSave ? 'pointer' : 'default';

      for (const [id, { button, label }] of aircraftButtons) {
        const selected = id === state.aircraftId;
        button.style.borderColor = selected ? ACCENT : 'rgba(140, 232, 255, 0.28)';
        // A trailing marker rather than a colour change alone, so selection does not rely on colour.
        button.textContent = selected ? `${label}  ·` : label;
      }

      invertPitch.box.checked = Boolean(state.settings.invertPitch);
      showOverlay.box.checked = Boolean(state.settings.showOverlay);

      bloomSlider.slider.value = String(state.settings.bloomStrength);
      bloomSlider.readout.textContent = Number(state.settings.bloomStrength).toFixed(2);

      backdrop.style.display = 'flex';
      visible = true;

      /*
        Focus the panel rather than a control. Focusing a button would make Enter re-trigger it, and
        focusing the seed field would send every subsequent keystroke there — including the ones the
        input layer deliberately steps aside for.
      */
      panel.focus();
    },

    /** Hide the menu. */
    close() {
      backdrop.style.display = 'none';
      visible = false;
    },

    /** Whether the menu is showing. The caller uses this to decide whether to advance the simulation. */
    isOpen() {
      return visible;
    },

    /**
     * Report the outcome of an action, in place of the button that caused it.
     *
     * Shown on the button itself rather than in a separate status line, because that is where the
     * player is already looking.
     *
     * @param {'save'|'load'} action
     * @param {string} text
     */
    reportResult(action, text) {
      const button = action === 'save' ? saveButton : loadButton;
      const original = action === 'save' ? 'Save world' : 'Load save';

      /*
        Cleared first. Pressing Save twice in quick succession would otherwise let the first timer
        restore the label while the second message was still meant to be showing.
      */
      clearTimeout(resultTimers.get(button));

      button.textContent = text;
      resultTimers.set(
        button,
        setTimeout(() => {
          button.textContent = original;
          resultTimers.delete(button);
        }, HUD.MESSAGE_SECONDS * MS_PER_SECOND),
      );
    },

    /** Remove the menu, its listeners, and any pending timer. */
    dispose() {
      for (const timer of resultTimers.values()) clearTimeout(timer);
      resultTimers.clear();
      backdrop.remove();
    },
  };
}
